import type { JSX } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";

import { api } from "../api";
import { Button, Card, Empty, ErrorNote, Loading, Pill } from "../components/Ui";
import { ago, datetime } from "../format";

/**
 * Beszélgetések — mit kérdeztek a látogatók a chattől, és mit kaptak.
 *
 * Ebből látszik, hol kell a tudásbázist vagy az AI-nak szóló utasításokat
 * pontosítani: mire nem tudott válaszolni a chat, mit értett félre.
 *
 * **Bizalmas.** A látogató bármit beírhat. Az azonosítók alakja (TAJ,
 * telefonszám, e-mail-cím, dátum) kitakarva tárolódik, a nevek nem szűrhetők
 * ki — ezért a nézet csak adminisztrátornak látszik, és a sorok a megőrzési
 * idő után törlődnek.
 */

export interface ChatItem {
  id: number;
  created_at: string;
  conversation: string;
  question: string;
  answer: string;
  status: string;
  detail: string;
  escalated: boolean;
  price_lookup: boolean;
  tools: string[];
  sources: { title: string; url: string }[];
  hits: string[];
  has_attachment: boolean;
  duration_ms: number;
}

interface ChatList {
  retention_days: number;
  summary: { total: number; escalated: number; problems: number; guarded: number };
  items: ChatItem[];
  next_before: number | null;
}

type Filter = "mind" | "tovabbiranyitva" | "problemas" | "kapuor";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "mind", label: "Mind" },
  { id: "tovabbiranyitva", label: "Továbbirányítva" },
  { id: "problemas", label: "Megszakadt, hibás" },
  { id: "kapuor", label: "Megállítva" },
];

const STATUS: Record<string, { tone: string; label: string }> = {
  megszakadt: { tone: "warn", label: "megszakadt" },
  helyreallitva: { tone: "muted", label: "megszakadt, helyreállt" },
  hiba: { tone: "bad", label: "hiba" },
  szuro: { tone: "warn", label: "biztonsági szűrő" },
  kapuor: { tone: "muted", label: "megállítva" },
  pontositas: { tone: "info", label: "pontosítás kérve" },
  kosar: { tone: "info", label: "kalkuláció módosítva" },
};

/**
 * A találatok oldalcímei, ismétlődés nélkül.
 *
 * A tárolt sorok a keresés pontszámait is hordozzák („Kapcsolat (s=0.61,
 * k=4.9)”) — az a hibakereséshez kell, a rendelőnek a cím a lényeg. A teljes
 * sor az egérmutató alatt olvasható.
 */
function pages(hits: string[]): string[] {
  return [...new Set(hits.map((hit) => hit.replace(/\s*\(s=[^)]*\)$/, "")))];
}

function query(params: Record<string, string | number | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== "") search.set(key, String(value));
  }
  return `/admin/v1/chats?${search.toString()}`;
}

export function ConversationsView(): JSX.Element {
  const [filter, setFilter] = useState<Filter>("mind");
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [thread, setThread] = useState<string | null>(null);
  const [data, setData] = useState<ChatList | null>(null);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(
    async (before: number | null): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const path = thread
          ? query({ conversation: thread })
          : query({ filter, q: search, before });
        const result = await api.get<ChatList>(path);
        setData(result);
        // Lapozásnál a korábbiak a lista VÉGÉRE kerülnek; új szűrésnél elölről.
        setItems((current) => (before ? [...current, ...result.items] : result.items));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [filter, search, thread],
  );

  useEffect(() => {
    setOpen(null);
    void load(null);
  }, [load]);

  const summary = data?.summary;

  return (
    <Card
      title={thread ? "Egy beszélgetés" : "Beszélgetések"}
      hint={
        thread
          ? "Időrendben, ahogy a látogató látta."
          : summary
            ? `Az elmúlt 7 napban ${summary.total} kérdés érkezett: ${summary.escalated} esetben a chat a rendelőhöz irányított, ${summary.problems} válasz megszakadt vagy hibára futott, ${summary.guarded} kérdést megállított a visszaélés-védelem.`
            : "Mit kérdeztek a látogatók a chattől, és mit kaptak."
      }
      actions={
        thread ? (
          <Button onClick={() => setThread(null)}>Vissza a listához</Button>
        ) : (
          <div className="segmented">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={filter === f.id ? "is-active" : ""}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )
      }
    >
      {data && (
        <p className="notice notice--info">
          Bizalmas adat. A beszélgetések {data.retention_days} nap után törlődnek. A
          TAJ-szám, a telefonszám, az e-mail-cím és a dátum kitakarva tárolódik; a látogató
          által beírt név nem szűrhető ki.
        </p>
      )}

      {!thread && (
        <form
          className="chat-search"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(draft.trim());
          }}
        >
          <input
            type="search"
            aria-label="Keresés a kérdésekben és a válaszokban"
            placeholder="Keresés a kérdésekben és a válaszokban…"
            value={draft}
            onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          />
          <button type="submit" className="btn">
            Keresés
          </button>
        </form>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && items.length === 0 && <Loading />}
      {data && !loading && items.length === 0 && (
        <Empty>
          {filter === "mind" && !search
            ? "Még nincs beszélgetés. Amint egy látogató kérdez a chattől, itt megjelenik."
            : "Nincs a szűrésnek megfelelő beszélgetés."}
        </Empty>
      )}

      {items.length > 0 && (
        <ul className="notifs">
          {items.map((item) => (
            <ChatRow
              key={item.id}
              item={item}
              open={thread !== null || open === item.id}
              inThread={thread !== null}
              onToggle={() => setOpen(open === item.id ? null : item.id)}
              onThread={() => setThread(item.conversation)}
            />
          ))}
        </ul>
      )}

      {!thread && data?.next_before && (
        <p className="chat-more">
          <Button onClick={() => void load(data.next_before)} disabled={loading}>
            {loading ? "Betöltés…" : "Korábbiak"}
          </Button>
        </p>
      )}
    </Card>
  );
}

function ChatRow({
  item,
  open,
  inThread,
  onToggle,
  onThread,
}: {
  item: ChatItem;
  open: boolean;
  inThread: boolean;
  onToggle: () => void;
  onThread: () => void;
}): JSX.Element {
  const status = STATUS[item.status];
  return (
    <li className={`notif chat${open ? " is-open" : ""}`}>
      <button type="button" className="notif__head" aria-expanded={open} onClick={onToggle}>
        <span className="notif__main">
          <span className="notif__title chat__question">
            {item.question || "(csak csatolt dokumentum)"}
          </span>
          <span className="notif__meta">
            {status && <Pill tone={status.tone}>{status.label}</Pill>}
            {item.escalated && <Pill tone="warn">a rendelőhöz irányítva</Pill>}
            {item.price_lookup && <Pill tone="info">árlekérdezés</Pill>}
            {item.has_attachment && <Pill tone="muted">csatolmány</Pill>}
          </span>
        </span>
        <span className="notif__time" title={datetime(item.created_at)}>
          {inThread ? datetime(item.created_at) : ago(item.created_at)}
        </span>
      </button>

      {open && (
        <div className="notif__body">
          <p className="notif__text chat__answer">{item.answer || "(nem érkezett válasz)"}</p>
          {item.detail && <p className="muted notif__facts">{item.detail}</p>}
          {item.sources.length > 0 && (
            <p className="muted notif__facts">
              Forrásként megjelent:{" "}
              {item.sources.map((source, index) => (
                <span key={source.url}>
                  {index > 0 && ", "}
                  <a href={source.url} target="_blank" rel="noreferrer noopener">
                    {source.title}
                  </a>
                </span>
              ))}
            </p>
          )}
          {item.hits.length > 0 && (
            <p className="muted notif__facts" title={item.hits.join("\n")}>
              A chat ezekből az oldalakból dolgozott: {pages(item.hits).join(" · ")}
            </p>
          )}
          <p className="muted notif__facts">
            {datetime(item.created_at)}
            {item.duration_ms > 0 && <> · válaszidő: {(item.duration_ms / 1000).toFixed(1)} mp</>}
          </p>
          {!inThread && (
            <p className="chat-more">
              <Button tone="ghost" onClick={onThread}>
                A teljes beszélgetés
              </Button>
            </p>
          )}
        </div>
      )}
    </li>
  );
}
